/**
 * Saved plans side by side — the comparison across homes, from Home (owner's design, 17 Oct 2026).
 *
 * Modelling another home is the same three steps again; the comparison happens afterwards, here, on
 * whichever saved plans the reader ticked. One row per plan, columns in the words the plans use, the
 * chosen row marked, and — because a pick-list cannot promise "same John, same money" — a line that
 * says what every plan shares and flags, in amber, anything that differs (living costs, horizon,
 * care start), so "left to the family" is never misread as a room comparison when it is not one.
 *
 * Every figure is the saved plan's own, quoted; nothing is re-run.
 */
import React from 'react';
import DialogShell from '../../components/ui/DialogShell.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { Button } from '../../components/ui/index.tsx';
import ComparisonTable from '../../components/strategy/ComparisonTable.tsx';
import { savedPlanTitle } from '../../features/agedCare/planWords.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { SavedStrategy } from './dashboardConfig.tsx';

export interface ComparePlansModalProps {
    plans: SavedStrategy[];
    factFind: DraftFactFind;
    chosenId: string | number | null;
    onOpenPlan: (plan: SavedStrategy) => void;
    onChoose?: (plan: SavedStrategy) => void;
    onClose: () => void;
}

const homeWords = (plan: SavedStrategy): string => {
    const room = Number(plan.outputs?.roomCost) || 0;
    const name = String((plan as { homeName?: string | null }).homeName || '').trim();
    const roomWords = room > 0 ? `${formatCurrency(room)} room` : 'room';
    return name ? `${name} · ${roomWords}` : roomWords;
};

type Basis = { living: number | null; years: number | null; start: string | null };
const basisOf = (plan: SavedStrategy): Basis => {
    const inputs = (plan.inputs || {}) as { scenarioLivingExpenses?: unknown; legacyYears?: unknown; careStartDate?: unknown };
    const living = inputs.scenarioLivingExpenses != null ? Number(inputs.scenarioLivingExpenses) : plan.outputs?.livingExpenses != null ? Number(plan.outputs.livingExpenses) : null;
    const years = inputs.legacyYears != null ? Number(inputs.legacyYears) : null;
    const start = inputs.careStartDate ? String(inputs.careStartDate) : null;
    return { living: Number.isFinite(living as number) ? (living as number) : null, years: Number.isFinite(years as number) ? (years as number) : null, start };
};

const ComparePlansModal = ({ plans, factFind, chosenId, onOpenPlan, onChoose, onClose }: ComparePlansModalProps) => {
    const titleOf = (plan: SavedStrategy): string => savedPlanTitle(plan as never, factFind);

    const bases = plans.map(basisOf);
    // A difference is two RECORDED values that disagree; a plan saved before a value was recorded
    // (null) is said to be unrecorded, never counted as different.
    const recorded = (pick: (b: Basis) => unknown) => bases.map(pick).filter((v) => v != null);
    const same = (pick: (b: Basis) => unknown) => new Set(recorded(pick).map((v) => JSON.stringify(v))).size <= 1;
    const livingSame = same((b) => b.living);
    const yearsSame = same((b) => b.years);
    const startSame = same((b) => b.start ?? 'now');
    const first = bases[0];
    const firstLiving = recorded((b) => b.living)[0] as number | undefined;
    const firstYears = recorded((b) => b.years)[0] as number | undefined;
    const unrecorded = bases.some((b) => b.years == null);
    const shared: string[] = [];
    if (livingSame && firstLiving != null) shared.push(`living costs ${formatCurrency(Math.round(firstLiving))} a year once in care`);
    if (yearsSame && firstYears != null && !unrecorded) shared.push(`about ${firstYears} ${firstYears === 1 ? 'year' : 'years'} of care`);
    if (startSame) shared.push(first?.start ? `care starting ${new Date(first.start).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}` : 'care starting now');
    const differ: string[] = [];
    if (!livingSame) differ.push('living costs');
    if (!yearsSame) differ.push('planning horizons');
    if (!startSame) differ.push('care start dates');

    return (
        <DialogShell
            titleId="compare-saved-plans-heading"
            testId="compare-saved-plans-dialog"
            size="xl"
            eyebrow="The plans you ticked on Home"
            title={plans.length === 2 ? 'Two homes, side by side' : `${plans.length} plans, side by side`}
            intro="The line under the table says which assumptions they share and flags any that differ."
            onClose={onClose}
            footer={
                <>
                    {onChoose && plans.filter((p) => chosenId == null || String(p.id) !== String(chosenId)).length === 1 && (
                        <Button type="button" variant="primary" onClick={() => onChoose(plans.find((p) => chosenId == null || String(p.id) !== String(chosenId))!)}>
                            Choose the other plan instead
                        </Button>
                    )}
                    <Button type="button" variant="secondary" onClick={onClose}>Back to Home</Button>
                </>
            }
        >
                <div>
                    <ComparisonTable
                        testId="compare-saved-plans-table"
                        firstHeading="Plan · home"
                        columns={[
                            { key: 'legacy', label: 'Left to the family', best: 'max', headline: true },
                            { key: 'cf', label: 'Each year, short by', best: 'max' },
                            { key: 'pension', label: 'Age Pension a year', best: 'max' },
                            { key: 'care', label: 'Care costs a year', best: 'min' },
                            { key: 'deposit', label: 'Tied up in the deposit', bar: 'amber' },
                        ]}
                        rows={plans.map((p) => {
                            const isChosen = chosenId != null && String(p.id) === String(chosenId);
                            const legacy = Number(p.outputs?.legacyEstimate) || 0;
                            const base = (p as { baseline?: { legacyValue?: number | null } | null }).baseline;
                            const delta = base?.legacyValue != null ? legacy - Number(base.legacyValue) : null;
                            const cf = Number(p.outputs?.cashFlow) || 0;
                            const row = p.strategy as { radPaidNet?: number } | undefined;
                            const deposit = Math.max(0, Number(row?.radPaidNet) || 0);
                            return {
                                key: String(p.id), chosen: isChosen, title: titleOf(p), subtitle: homeWords(p), onOpen: () => onOpenPlan(p),
                                cells: {
                                    legacy: { value: legacy, sub: delta != null ? <span className={delta >= 0 ? 'text-emerald-700' : 'text-rose-700'}>{delta >= 0 ? '+' : '−'}{formatCurrency(Math.abs(Math.round(delta)))} vs the comparison</span> : undefined },
                                    cf: { value: cf, display: cf < 0 ? formatCurrency(Math.abs(Math.round(cf))) : `+${formatCurrency(Math.round(cf))}`, tone: cf < 0 ? 'neutral' : 'good' },
                                    pension: { value: Math.round(Number(p.outputs?.agePension) || 0) },
                                    care: { value: Math.round(Number(p.outputs?.agedCareCost) || 0) },
                                    deposit: { value: Math.round(deposit) },
                                },
                            };
                        })}
                    />
                    <div className="mt-3 rounded-xl border border-slate-200 px-4 py-3 text-[13px] text-slate-700" data-testid="compare-saved-plans-basis">
                        {shared.length > 0 && <p><strong>Same for every plan here:</strong> {shared.join(' · ')}.</p>}
                        {unrecorded && <p className="mt-1 text-slate-500">One of these plans was saved before its planning horizon was recorded, so the horizon is not compared.</p>}
                        {differ.length > 0 && (
                            <p className="mt-1 rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white px-3 py-2">
                                <strong>Not a like-for-like comparison:</strong> these plans were modelled with different {differ.join(' and ')}, so &quot;left to the family&quot; is not only a room comparison. Open a plan to change its assumptions and model it again.
                            </p>
                        )}
                    </div>
                </div>
        </DialogShell>
    );
};

export default ComparePlansModal;
